import React, { useCallback, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CustomSelect from 'components/form/CustomSelect';
import TemplateGroupView, { TemplateGroup } from 'pages/contract/components/TemplateGroupView';
import {
  CreateTicketFormSelectItemInput,
  GetTicketTypeResponse,
  TicketFormItemResult,
  UpdateTicketFormSelectItemInput,
  createTicketFormGroup,
  createTicketFormItem,
  deleteTicketFormItem,
  getTicketType,
  updateTicketFormGroup,
  updateTicketFormGroupDisplay,
  updateTicketFormItem,
  updateTicketFormItemDisplay,
} from 'stores/TicketTypeStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

// フォーム種別（master_type: form_type と同義。契約テンプレと同じ選択肢）
const FORM_TYPE_OPTIONS = [
  { value: 'TEXT', label: 'テキスト' },
  { value: 'KANA_TEXT', label: 'カナテキスト' },
  { value: 'NUMBER_TEXT', label: '数値テキスト' },
  { value: 'SELECT', label: 'セレクト' },
  { value: 'MULTI_SELECT', label: 'マルチセレクト' },
  { value: 'RADIO', label: 'ラジオ' },
  { value: 'CHECK', label: 'チェック' },
  { value: 'MULTI_CHECK', label: 'マルチチェック' },
  { value: 'MULTI_CHECK_BUTTON', label: 'マルチチェックボタン' },
  { value: 'DATE_PICKER_START', label: '日付ピッカー（開始）' },
  { value: 'DATE_PICKER_END', label: '日付ピッカー（終了）' },
];
const SELECT_FORM_TYPES = [
  'SELECT',
  'MULTI_SELECT',
  'RADIO',
  'CHECK',
  'MULTI_CHECK',
  'MULTI_CHECK_BUTTON',
];

type SelectedNode =
  { type: 'item'; itemId: string } | { type: 'new-item'; groupId: string | null } | null;

type Props = {
  /** 対象種別ID（null=未選択。設定画面で種別未選択時） */
  typeId: string | null;
  /** 対象種別が有効中か（有効中は編集不可） */
  isActive: boolean;
  /** 編集可能か（GLOBAL種別のロール制約等） */
  canEdit: boolean;
  /** 左ペイン上部に差し込むスロット（種別セレクタ等） */
  headerSlot?: React.ReactNode;
  /** typeId=null 時に左ペインに表示する案内文 */
  emptyText?: string;
};

/**
 * チケット種別のフォームテンプレート編集（2ペイン）。
 * 契約の TemplateManagement を流用。左=(headerSlot＋)グループ/項目ツリー（D&D並び替え）、
 * 右=フォーム編集/プレビュー（タブ）または項目編集。
 * 有効中種別は編集不可（無効化してから編集）。
 */
const TicketTypeFormEditor: React.FC<Props> = ({
  typeId,
  isActive,
  canEdit,
  headerSlot,
  emptyText,
}) => {
  const [data, setData] = useState<GetTicketTypeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SelectedNode>(null);

  const readOnly = isActive || !canEdit;

  const fetchData = useCallback(() => {
    if (!typeId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    getTicketType({ typeId, visibleOnly: false })
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch(() => {
        errorDefault('フォームテンプレートの取得に失敗しました');
        setLoading(false);
      });
  }, [typeId]);

  useEffect(() => {
    fetchData();
    setSelected(null);
  }, [fetchData]);

  const handleAddGroup = () => {
    createTicketFormGroup({ typeId: typeId as string, name: '新しいグループ' })
      .then(() => {
        successDefault('グループを追加しました');
        fetchData();
      })
      .catch((e) => errorDefault('グループの追加に失敗しました', toErrorDetail(e)));
  };

  const handleRenameGroup = (groupId: string, name: string) => {
    updateTicketFormGroup({ groupId, name })
      .then(() => {
        successDefault('グループ名を更新しました');
        fetchData();
      })
      .catch((e) => errorDefault('グループ名の更新に失敗しました', toErrorDetail(e)));
  };

  // 項目のグループ間/内 移動（表示設定APIで永続化・楽観更新はしない簡易版）
  const handleItemDrop = (itemId: string, targetGroupId: string | null, targetIndex: number) => {
    if (!data || readOnly) return;
    const targetGroup = data.results.find((g) => g.groupId === targetGroupId);
    const targetItems = targetGroup
      ? targetGroup.items.filter((i) => !i.deleted).sort((a, b) => a.sortOrder - b.sortOrder)
      : [];
    const filtered = targetItems.filter((i) => i.itemId !== itemId);
    filtered.splice(targetIndex, 0, { itemId } as TicketFormItemResult);

    const items =
      targetGroupId === null
        ? [{ itemId, groupId: null, sortOrder: -1 }]
        : filtered.map((it, i) => ({
            itemId: it.itemId,
            groupId: targetGroupId,
            sortOrder: i + 1,
          }));

    updateTicketFormItemDisplay({ typeId: typeId as string, items })
      .then(fetchData)
      .catch((e) => {
        errorDefault('移動の保存に失敗しました', toErrorDetail(e));
        fetchData();
      });
  };

  // 種別未選択: 左ペインに headerSlot（セレクタ）＋案内、右ペインは空表示
  if (!typeId) {
    return (
      <Grid container spacing={1.5} sx={{ height: '100%' }}>
        <Grid size={5} sx={{ height: '100%', overflow: 'hidden' }}>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              height: '100%',
              overflow: 'auto',
              p: 1.5,
            }}
          >
            {headerSlot}
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              {emptyText ?? '設定を選択または追加してください'}
            </Typography>
          </Box>
        </Grid>
        <Grid size={7} sx={{ height: '100%', overflow: 'hidden' }}>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              左の設定を選択すると、フォームの編集・プレビューができます
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (loading || !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  // 全グループを表示（新規作成グループは sort_order=-1 で返るため、
  // >=0 で絞ると表示されない不具合になる）。表示中(>=0)を先に、非表示(-1)を後ろに並べる。
  const allGroups = [...data.results].sort((a, b) => {
    const av = a.sortOrder < 0 ? Number.MAX_SAFE_INTEGER : a.sortOrder;
    const bv = b.sortOrder < 0 ? Number.MAX_SAFE_INTEGER : b.sortOrder;
    return av - bv;
  });
  const unassigned = data.unassignedItems.filter((i) => !i.deleted);

  // グループの表示/非表示トグル（表示設定APIで sort_order を一括更新）
  const handleToggleGroupVisible = (groupId: string, visible: boolean) => {
    if (readOnly) return;
    const currentVisible = data.results
      .filter((g) => g.sortOrder >= 0 && g.groupId !== groupId)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((g) => g.groupId);
    const nextVisible = visible ? [...currentVisible, groupId] : currentVisible;
    updateTicketFormGroupDisplay({
      typeId: typeId as string,
      groups: nextVisible.map((gid, i) => ({ groupId: gid, sortOrder: i })),
    })
      .then(fetchData)
      .catch((e) => {
        errorDefault('表示設定の保存に失敗しました', toErrorDetail(e));
        fetchData();
      });
  };

  return (
    <Grid container spacing={1.5} sx={{ height: '100%' }}>
      {/* 左ペイン: (セレクタ)＋フォームツリー */}
      <Grid size={5} sx={{ height: '100%', overflow: 'hidden' }}>
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            height: '100%',
            overflow: 'auto',
            p: 1.5,
          }}
        >
          {headerSlot}

          {isActive && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              有効中はフォームを編集できません。編集するには無効化してください。
            </Alert>
          )}
          {!isActive && !canEdit && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              この設定を編集する権限がありません（参照のみ）。
            </Alert>
          )}

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              フォーム項目
            </Typography>
            {!readOnly && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddGroup}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                グループ追加
              </Button>
            )}
          </Stack>

          <Box>
            {allGroups.map((group) => (
              <GroupNode
                key={group.groupId}
                group={group}
                selected={selected}
                onSelect={setSelected}
                onItemDrop={handleItemDrop}
                onToggleVisible={handleToggleGroupVisible}
                onRename={handleRenameGroup}
                readOnly={readOnly}
              />
            ))}
            {/* 未所属 */}
            <Accordion
              elevation={0}
              defaultExpanded
              sx={{ '&:before': { display: 'none' }, border: 0 }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1, minHeight: 36 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    未所属項目 ({unassigned.length})
                  </Typography>
                  {!readOnly && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected({ type: 'new-item', groupId: null });
                      }}
                      sx={{ color: 'primary.main' }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails
                sx={{ px: 1, pt: 0 }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  try {
                    const d = JSON.parse(e.dataTransfer.getData('text/plain'));
                    handleItemDrop(d.itemId, null, 0);
                  } catch {
                    /* noop */
                  }
                }}
              >
                <Stack spacing={0} sx={{ minHeight: 24 }}>
                  {unassigned.map((item) => (
                    <ItemRow
                      key={item.itemId}
                      item={item}
                      selected={selected}
                      onSelect={setSelected}
                      readOnly={readOnly}
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Grid>

      {/* 右ペイン: フォーム編集/プレビュー、または項目編集 */}
      <Grid size={7} sx={{ height: '100%', overflow: 'hidden' }}>
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            height: '100%',
            overflow: 'auto',
          }}
        >
          <RightPane
            data={data}
            selected={selected}
            typeId={typeId as string}
            readOnly={readOnly}
            onRefresh={fetchData}
            onCancel={() => setSelected(null)}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

// --- グループノード ---
const GroupNode: React.FC<{
  group: GetTicketTypeResponse['results'][number];
  selected: SelectedNode;
  onSelect: (n: SelectedNode) => void;
  onItemDrop: (itemId: string, targetGroupId: string | null, targetIndex: number) => void;
  onToggleVisible: (groupId: string, visible: boolean) => void;
  onRename: (groupId: string, name: string) => void;
  readOnly: boolean;
}> = ({ group, selected, onSelect, onItemDrop, onToggleVisible, onRename, readOnly }) => {
  const isVisible = group.sortOrder >= 0;
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState(group.name);
  const activeItems = useMemo(
    () => group.items.filter((i) => !i.deleted).sort((a, b) => a.sortOrder - b.sortOrder),
    [group.items],
  );

  const commitRename = () => {
    const next = draftName.trim();
    if (next && next !== group.name) onRename(group.groupId, next);
    setRenaming(false);
  };

  return (
    <Accordion
      elevation={0}
      defaultExpanded
      sx={{ '&:before': { display: 'none' }, border: 0 }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        try {
          const d = JSON.parse(e.dataTransfer.getData('text/plain'));
          onItemDrop(d.itemId, group.groupId, activeItems.length);
        } catch {
          /* noop */
        }
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, minHeight: 40 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
          {isVisible ? (
            <FolderIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          ) : (
            <FolderOffIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          )}
          {renaming ? (
            <TextField
              size="small"
              value={draftName}
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') {
                  setDraftName(group.name);
                  setRenaming(false);
                }
              }}
              slotProps={{ htmlInput: { maxLength: 200 } }}
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          ) : (
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{ color: isVisible ? 'text.primary' : 'text.disabled' }}
            >
              {group.name}
            </Typography>
          )}
          <Chip
            size="small"
            label={isVisible ? '表示中' : '非表示'}
            variant={isVisible ? 'filled' : 'outlined'}
            color={isVisible ? 'success' : undefined}
            sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }}
          />
          <Box sx={{ flex: 1 }} />
          {!readOnly && !renaming && (
            <>
              <Tooltip title="グループ名を編集">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDraftName(group.name);
                    setRenaming(true);
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  <EditIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={isVisible ? '非表示にする' : '表示にする'}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleVisible(group.groupId, !isVisible);
                  }}
                  sx={{ color: 'text.secondary' }}
                >
                  {isVisible ? (
                    <VisibilityOffIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <VisibilityIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect({ type: 'new-item', groupId: group.groupId });
                }}
                sx={{ color: 'primary.main' }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 0.5 }}>
        <Stack spacing={0}>
          {activeItems.map((item) => (
            <ItemRow
              key={item.itemId}
              item={item}
              selected={selected}
              onSelect={onSelect}
              readOnly={readOnly}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

// --- アイテム行 ---
const ItemRow: React.FC<{
  item: TicketFormItemResult;
  selected: SelectedNode;
  onSelect: (n: SelectedNode) => void;
  readOnly: boolean;
}> = ({ item, selected, onSelect, readOnly }) => {
  const isSelected = selected?.type === 'item' && selected.itemId === item.itemId;
  return (
    <Box
      draggable={!readOnly}
      onDragStart={(e) =>
        e.dataTransfer.setData('text/plain', JSON.stringify({ itemId: item.itemId }))
      }
      onClick={() => onSelect({ type: 'item', itemId: item.itemId })}
      sx={{
        display: 'flex',
        alignItems: 'center',
        pl: 2.5,
        pr: 1,
        py: 0.75,
        ml: 1,
        borderRadius: 1,
        cursor: 'pointer',
        bgcolor: isSelected ? 'action.selected' : 'transparent',
        borderLeft: isSelected ? 2 : 0,
        borderColor: 'primary.main',
        '&:hover': { bgcolor: 'action.hover' },
        transition: 'background-color 0.2s',
      }}
    >
      {!readOnly && (
        <DragIndicatorIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'grab', mr: 1 }} />
      )}
      <TextFieldsIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 1 }} />
      <Typography variant="body2" sx={{ flex: 1 }} noWrap>
        {item.label}
      </Typography>
    </Box>
  );
};

// --- 右ペイン ---
const RightPane: React.FC<{
  data: GetTicketTypeResponse;
  selected: SelectedNode;
  typeId: string;
  readOnly: boolean;
  onRefresh: () => void;
  onCancel: () => void;
}> = ({ data, selected, typeId, readOnly, onRefresh, onCancel }) => {
  // 何も選択していない時は「フォーム編集/プレビュー」タブ（申請フォーム設定と同じ）
  if (!selected) {
    return <FormPreviewPanel data={data} />;
  }

  if (selected.type === 'new-item') {
    return (
      <ItemCreatePanel
        typeId={typeId}
        groupId={selected.groupId}
        onRefresh={onRefresh}
        onCancel={onCancel}
      />
    );
  }

  // item edit
  const allItems: TicketFormItemResult[] = [
    ...data.results.flatMap((g) => g.items),
    ...data.unassignedItems,
  ];
  const item = allItems.find((i) => i.itemId === selected.itemId);
  if (!item) return null;
  return <ItemEditPanel item={item} readOnly={readOnly} onRefresh={onRefresh} />;
};

// --- フォーム編集/プレビュー（申請フォーム設定と同じ TemplateGroupView を流用） ---
const FormPreviewPanel: React.FC<{ data: GetTicketTypeResponse }> = ({ data }) => {
  const [tab, setTab] = useState(0);
  const [values, setValues] = useState<Record<string, string>>({});

  // 表示中グループ（sort_order>=0）＋未所属項目を TemplateGroupView 用に変換
  const groups: TemplateGroup[] = useMemo(() => {
    const g: TemplateGroup[] = data.results
      .filter((grp) => grp.sortOrder >= 0)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((grp) => ({
        groupId: grp.groupId,
        name: grp.name,
        items: grp.items
          .filter((it) => !it.deleted)
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((it) => ({
            itemKey: it.itemKey,
            label: it.label,
            formType: it.formType,
            selectItems: (it.selectItems || [])
              .filter((si) => !si.deleted)
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((si) => ({ code: si.code, name: si.name })),
          })),
      }));
    const unassignedActive = data.unassignedItems.filter((it) => !it.deleted);
    if (unassignedActive.length > 0) {
      g.push({
        groupId: '__unassigned__',
        name: 'その他項目',
        items: unassignedActive.map((it) => ({
          itemKey: it.itemKey,
          label: it.label,
          formType: it.formType,
          selectItems: (it.selectItems || [])
            .filter((si) => !si.deleted)
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((si) => ({ code: si.code, name: si.name })),
        })),
      });
    }
    return g;
  }, [data]);

  return (
    <Box sx={{ p: 2 }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, minHeight: 40 }}
      >
        <Tab label="編集イメージ" sx={{ textTransform: 'none', minHeight: 40 }} />
        <Tab label="プレビュー" sx={{ textTransform: 'none', minHeight: 40 }} />
      </Tabs>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
        左のツリーで項目を選択すると編集できます。ここはチケット作成時のフォームの見え方を確認できます。
      </Typography>
      <TemplateGroupView
        groups={groups}
        mode={tab === 0 ? 'edit' : 'preview'}
        values={values}
        onValueChange={(k, v) => setValues((prev) => ({ ...prev, [k]: v }))}
      />
    </Box>
  );
};

// --- 新規項目作成 ---
const ItemCreatePanel: React.FC<{
  typeId: string;
  groupId: string | null;
  onRefresh: () => void;
  onCancel: () => void;
}> = ({ typeId, groupId, onRefresh, onCancel }) => {
  const [itemKey, setItemKey] = useState('');
  const [label, setLabel] = useState('');
  const [formType, setFormType] = useState('');
  const [selectItems, setSelectItems] = useState<CreateTicketFormSelectItemInput[]>([]);

  const showSelect = SELECT_FORM_TYPES.includes(formType);
  const valid = itemKey.trim() && label.trim() && formType && /^[a-zA-Z0-9_-]+$/.test(itemKey);

  const handleCreate = () => {
    createTicketFormItem({
      typeId,
      itemKey: itemKey.trim(),
      label: label.trim(),
      formType,
      groupId: groupId || undefined,
      selectItems: showSelect && selectItems.length > 0 ? selectItems : undefined,
    })
      .then(() => {
        successDefault('項目を追加しました');
        onRefresh();
        onCancel();
      })
      .catch((e) => errorDefault('項目の追加に失敗しました', toErrorDetail(e)));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle2" fontWeight={700}>
          項目の新規作成
        </Typography>
        <TextField
          size="small"
          fullWidth
          label="キー"
          value={itemKey}
          onChange={(e) => setItemKey(e.target.value)}
          helperText="半角英数字 + - + _ のみ"
          error={!!itemKey && !/^[a-zA-Z0-9_-]*$/.test(itemKey)}
          slotProps={{ htmlInput: { maxLength: 100 } }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <TextField
          size="small"
          fullWidth
          label="ラベル"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          slotProps={{ htmlInput: { maxLength: 200 } }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <CustomSelect
          mode="single"
          label="フォーム種別"
          options={FORM_TYPE_OPTIONS}
          value={formType}
          onChange={setFormType}
          placeholder="種別を選択"
        />
        {showSelect && <SelectItemsEditor items={selectItems} onChange={setSelectItems} />}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button onClick={onCancel} sx={{ textTransform: 'none', fontWeight: 600 }}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            disabled={!valid}
            onClick={handleCreate}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            追加
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

// --- 項目編集 ---
const ItemEditPanel: React.FC<{
  item: TicketFormItemResult;
  readOnly: boolean;
  onRefresh: () => void;
}> = ({ item, readOnly, onRefresh }) => {
  const [label, setLabel] = useState(item.label);
  const [editItems, setEditItems] = useState<UpdateTicketFormSelectItemInput[]>([]);
  const showSelect = SELECT_FORM_TYPES.includes(item.formType);

  useEffect(() => {
    setLabel(item.label);
    setEditItems(
      (item.selectItems || [])
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((si) => ({
          selectItemId: si.selectItemId,
          code: si.code,
          name: si.name,
          sortOrder: si.sortOrder,
          isDeleted: si.deleted,
        })),
    );
  }, [item]);

  const handleUpdate = () => {
    updateTicketFormItem({
      itemId: item.itemId,
      label,
      selectItems: showSelect ? editItems : undefined,
    })
      .then(() => {
        successDefault('項目を更新しました');
        onRefresh();
      })
      .catch((e) => errorDefault('項目の更新に失敗しました', toErrorDetail(e)));
  };

  const handleDelete = () => {
    deleteTicketFormItem({ itemId: item.itemId })
      .then(() => {
        successDefault('項目を削除しました');
        onRefresh();
      })
      .catch((e) => errorDefault('項目の削除に失敗しました', toErrorDetail(e)));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle2" fontWeight={700}>
          項目の編集
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            キー:
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {item.itemKey}
          </Typography>
        </Stack>
        <TextField
          size="small"
          fullWidth
          label="ラベル"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={readOnly}
          slotProps={{ htmlInput: { maxLength: 200 } }}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        {showSelect && (
          <SelectItemsEditor
            items={editItems}
            editable={!readOnly}
            update
            onChange={(v) => setEditItems(v as UpdateTicketFormSelectItemInput[])}
          />
        )}
        {!readOnly && (
          <Stack direction="row" spacing={1} justifyContent="space-between">
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              削除
            </Button>
            <Button
              variant="contained"
              onClick={handleUpdate}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
            >
              更新
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

// --- 選択肢エディタ（新規/編集共用） ---
type SelectItemLike = CreateTicketFormSelectItemInput | UpdateTicketFormSelectItemInput;
const SelectItemsEditor: React.FC<{
  items: SelectItemLike[];
  editable?: boolean;
  update?: boolean;
  onChange: (items: SelectItemLike[]) => void;
}> = ({ items, editable = true, update = false, onChange }) => {
  const visible = update
    ? (items as UpdateTicketFormSelectItemInput[]).filter((i) => !i.isDeleted)
    : items;

  const add = () => {
    const order = visible.length + 1;
    if (update) {
      onChange([
        ...(items as UpdateTicketFormSelectItemInput[]),
        { selectItemId: null, code: '', name: '', sortOrder: order, isDeleted: false },
      ]);
    } else {
      onChange([
        ...(items as CreateTicketFormSelectItemInput[]),
        { code: '', name: '', sortOrder: order },
      ]);
    }
  };

  const updateField = (idx: number, field: 'code' | 'name', value: string) => {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  };

  const remove = (idx: number) => {
    if (update) {
      const next = [...(items as UpdateTicketFormSelectItemInput[])];
      const target = next[idx];
      if (target.selectItemId) next[idx] = { ...target, isDeleted: true };
      else next.splice(idx, 1);
      onChange(next);
    } else {
      const next = [...items];
      next.splice(idx, 1);
      onChange(next);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          選択肢
        </Typography>
        {editable && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={add}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            追加
          </Button>
        )}
      </Stack>
      <Stack spacing={1}>
        {items.map((si, idx) => {
          if (update && (si as UpdateTicketFormSelectItemInput).isDeleted) return null;
          const isExisting = update && !!(si as UpdateTicketFormSelectItemInput).selectItemId;
          return (
            <Stack key={idx} direction="row" spacing={1} alignItems="center">
              <TextField
                size="small"
                placeholder="code"
                value={si.code}
                onChange={(e) => updateField(idx, 'code', e.target.value)}
                disabled={!editable || isExisting}
                slotProps={{ htmlInput: { maxLength: 50 } }}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                placeholder="name"
                value={si.name}
                onChange={(e) => updateField(idx, 'name', e.target.value)}
                disabled={!editable}
                slotProps={{ htmlInput: { maxLength: 200 } }}
                sx={{ flex: 1 }}
              />
              {editable && (
                <IconButton
                  size="small"
                  onClick={() => remove(idx)}
                  sx={{ color: 'text.disabled' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export default TicketTypeFormEditor;
